/* ==========================================================================
   Y1 SPORT - SIZE CHART DATA
   ==========================================================================
   THIS IS THE ONLY FILE YOU NEED TO EDIT TO CHANGE SIZING.
   The size finder reads everything from here. No other file needs touching.

   ---------------------------------------------------------------------------
   TO CHANGE A NUMBER
   ---------------------------------------------------------------------------
   Find the chart, find the size row, change the number. Ranges are written
   [min, max] and are always in CENTIMETRES. Save. Done.

   ---------------------------------------------------------------------------
   TO ADD A NEW SIZE CHART
   ---------------------------------------------------------------------------
   Copy any existing chart block, paste it into the list below, then set:
     key      - a short unique id, lowercase_with_underscores
     label    - the name customers see
     tags     - the Shopify product tag(s) that switch this chart on
     primary  - the measurement this garment is really cut to (see below)
     profile  - mens / womens / unisex / junior (drives the height advice)
     measures - which body measurements this garment is sized on
     sizes    - the rows of the chart
   Save. It works immediately - no other change needed anywhere.

   ---------------------------------------------------------------------------
   WHAT "primary" MEANS - THIS IS THE IMPORTANT ONE
   ---------------------------------------------------------------------------
   Garments are cut to ONE main measurement. Tops are cut to the chest;
   bottoms are cut to the waist. This mirrors the international sizing
   standard (ISO 8559-2), which designates a top by chest girth with waist
   as a SECONDARY dimension.

   The finder uses this so a slightly-bigger waist doesn't drag someone up
   a size they don't need. If the secondary measurement is only ONE size
   above the primary, we stay on the primary size. Only when the two are
   TWO or more sizes apart do we move up, and then only halfway - and we
   say so clearly and open the full chart so they can judge for themselves.

   ---------------------------------------------------------------------------
   IMPORTANT: ORDER MATTERS
   ---------------------------------------------------------------------------
   Charts are checked TOP TO BOTTOM and the FIRST tag match wins. Keep specific
   tags above generic ones. This is why "Therma Bottoms" sits at the bottom.

   ---------------------------------------------------------------------------
   MEASUREMENT KEYS
   ---------------------------------------------------------------------------
   chest / waist / hip / height   - used to work out the recommended size
   leg                            - shown in the chart only, never asked for
                                    (single number, not a range)
   Source of truth: "Body Measurement Size Guide.xlsx", verified Aug 2026.
   (This replaced the older BestFit export, which had rounded the AKI
   playing-shirt numbers to whole centimetres and lost the .5 values.)
   ========================================================================== */

window.Y1_SIZE_CHARTS = {

  /* ==========================================================================
     HEIGHT ADVICE THRESHOLDS (centimetres)
     ==========================================================================
     Our charts describe GIRTH (how wide round) but say nothing about garment
     LENGTH for adults. So height NEVER changes the recommended size - it only
     adds a note warning that a top may sit shorter/longer than intended, or
     that bottoms may run short/long in the leg. Changing size for length
     would just make the width wrong.

     The numbers below are roughly 1.5 standard deviations from UK adult
     average height (men ~175cm, women ~162cm) - i.e. the tallest and
     shortest ~7% of people. Raise them to make the note rarer, lower them to
     make it more common. Set a profile to null to switch that note off.
     ========================================================================== */
  heightAdvice: {
    mens:   { tall: 186, short: 165 },
    womens: { tall: 172, short: 152 },
    unisex: { tall: 186, short: 165 },
    junior: null   /* junior charts are already height-banded - no advice */
  },

  charts: [

    /* ===================== PLAYING SHIRTS - CURRENT ===================== */

    {
      key: "playing_shirt_mens_new",
      label: "Playing Shirt - Men's",
      tags: ["Mens Playing Shirts New"],
      primary: "chest",
      profile: "mens",
      measures: [
        { key: "chest", name: "Chest" },
        { key: "waist", name: "Waist" },
        { key: "hip",   name: "Hip" }
      ],
      sizes: [
        { size: "XS",    chest: [84, 89],     waist: [71, 76],       hip: [89, 94] },
        { size: "S",     chest: [89, 94],     waist: [76, 81.5],     hip: [94, 99] },
        { size: "M",     chest: [94, 99],     waist: [81.5, 86.5],   hip: [99, 104] },
        { size: "L",     chest: [99, 104],    waist: [86.5, 91.5],   hip: [104, 109] },
        { size: "XL",    chest: [104, 109],   waist: [91.5, 96.5],   hip: [109, 112] },
        { size: "XXL",   chest: [109, 117],   waist: [96.5, 106.5],  hip: [112, 119.5] },
        { size: "XXXL",  chest: [117, 124.5], waist: [106.5, 114.5], hip: [119.5, 127] },
        { size: "XXXXL", chest: [124.5, 132], waist: [114.5, 122],   hip: [127, 134.5] }
      ]
    },

    {
      key: "playing_shirt_womens_new",
      label: "Playing Shirt - Women's",
      tags: ["Womens Playing Shirts New"],
      primary: "chest",
      profile: "womens",
      measures: [
        { key: "chest", name: "Chest" },
        { key: "waist", name: "Waist" },
        { key: "hip",   name: "Hip" }
      ],
      sizes: [
        { size: "XS",    bracket: "UK 8",  chest: [84, 89],       waist: [71, 73.5],    hip: [89, 94] },
        { size: "S",     bracket: "UK 10", chest: [89, 91.5],     waist: [73.5, 79],    hip: [94, 99] },
        { size: "M",     bracket: "UK 12", chest: [91.5, 96.5],   waist: [79, 81.5],    hip: [99, 101.5] },
        { size: "L",     bracket: "UK 14", chest: [96.5, 99],     waist: [81.5, 86.5],  hip: [101.5, 106.5] },
        { size: "XL",    bracket: "UK 16", chest: [99, 104],      waist: [86.5, 89],    hip: [106.5, 109] },
        { size: "XXL",   bracket: "UK 18", chest: [104, 112],     waist: [89, 96.5],    hip: [109, 117] },
        { size: "XXXL",  bracket: "UK 20", chest: [112, 119.5],   waist: [96.5, 104],   hip: [117, 124.5] },
        { size: "XXXXL", bracket: "UK 22", chest: [119.5, 127],   waist: [104, 112],    hip: [124.5, 132] }
      ]
    },

    {
      key: "playing_shirt_juniors_new",
      label: "Playing Shirt - Juniors",
      tags: ["Junior Playing Shirts New"],
      primary: "chest",
      profile: "junior",
      measures: [
        { key: "chest", name: "Chest" },
        { key: "waist", name: "Waist" },
        { key: "hip",   name: "Hip" }
      ],
      sizes: [
        { size: "JXS", chest: [68.5, 72], waist: [51, 56],     hip: [68.5, 71] },
        { size: "JS",  chest: [71, 76],   waist: [56, 61],     hip: [71, 76] },
        { size: "JM",  chest: [76, 79],   waist: [61, 63.5],   hip: [76, 79] },
        { size: "JL",  chest: [79, 84],   waist: [63.5, 68.5], hip: [79, 84] },
        { size: "JXL", chest: [84, 88],   waist: [68.5, 71],   hip: [84, 89] }
      ]
    },

    /* ============ PLAYING SHIRTS - OLDER SPEC (pre-2025 kit) ============ */

    {
      key: "playing_shirt_mens_old",
      label: "Playing Shirt - Men's",
      tags: ["Mens Playing Shirts"],
      primary: "chest",
      profile: "mens",
      measures: [
        { key: "chest", name: "Chest" },
        { key: "waist", name: "Waist" }
      ],
      sizes: [
        { size: "XS",  chest: [84, 89],   waist: [70, 75] },
        { size: "S",   chest: [89, 94],   waist: [75, 80] },
        { size: "M",   chest: [94, 99],   waist: [80, 85] },
        { size: "L",   chest: [99, 104],  waist: [85, 90] },
        { size: "XL",  chest: [104, 109], waist: [90, 95] },
        { size: "2XL", chest: [109, 114], waist: [95, 100] },
        { size: "3XL", chest: [114, 119], waist: [100, 105] },
        { size: "4XL", chest: [119, 124], waist: [105, 110] }
      ]
    },

    {
      key: "playing_shirt_womens_old",
      label: "Playing Shirt - Women's",
      tags: ["Womens Playing Shirts"],
      primary: "chest",
      profile: "womens",
      measures: [
        { key: "chest", name: "Bust" },
        { key: "waist", name: "Waist" },
        { key: "hip",   name: "Hip" }
      ],
      sizes: [
        { size: "XS",  bracket: "UK 8",  chest: [79, 84],   waist: [62, 67],  hip: [87, 92] },
        { size: "S",   bracket: "UK 10", chest: [84, 89],   waist: [67, 72],  hip: [92, 97] },
        { size: "M",   bracket: "UK 12", chest: [89, 94],   waist: [72, 77],  hip: [97, 102] },
        { size: "L",   bracket: "UK 14", chest: [94, 99],   waist: [77, 82],  hip: [102, 107] },
        { size: "XL",  bracket: "UK 16", chest: [99, 104],  waist: [82, 87],  hip: [107, 112] },
        { size: "XXL", bracket: "UK 18", chest: [104, 109], waist: [87, 92],  hip: [112, 117] },
        { size: "3XL", bracket: "UK 20", chest: [109, 114], waist: [92, 97],  hip: [117, 122] },
        { size: "4XL", bracket: "UK 22", chest: [114, 119], waist: [97, 102], hip: [122, 127] }
      ]
    },

    {
      key: "playing_shirt_junior_old",
      label: "Playing Shirt - Junior",
      tags: ["Junior Playing Shirts"],
      /* This chart is organised by height band, so height leads here. */
      primary: "height",
      profile: "junior",
      measures: [
        { key: "height", name: "Height" },
        { key: "chest",  name: "Chest" },
        { key: "waist",  name: "Waist" }
      ],
      sizes: [
        { size: "JXS",  height: [116, 128], chest: [60, 64], waist: [54, 58] },
        { size: "JS",   height: [128, 140], chest: [64, 68], waist: [58, 62] },
        { size: "JM",   height: [140, 152], chest: [68, 72], waist: [62, 66] },
        { size: "JL",   height: [152, 164], chest: [72, 76], waist: [66, 70] },
        { size: "JXL",  height: [164, 170], chest: [76, 80], waist: [70, 74] },
        { size: "J2XL", height: [170, 176], chest: [80, 84], waist: [74, 78] }
      ]
    },

    /* ============================ GOALKEEPER ============================ */

    {
      key: "gk_smocks",
      label: "GK Smock",
      tags: ["GK Smocks"],
      primary: "chest",
      profile: "unisex",
      note: "Measure yourself while wearing your goalkeeper padding.",
      measures: [
        { key: "chest", name: "Chest" },
        { key: "waist", name: "Waist" }
      ],
      sizes: [
        { size: "Junior",  chest: [89, 94],   waist: [75, 80] },
        { size: "M/L",     chest: [99, 104],  waist: [85, 90] },
        { size: "XL/2XL",  chest: [109, 114], waist: [95, 100] },
        { size: "3XL/4XL", chest: [119, 124], waist: [105, 110] }
      ]
    },

    /* =============================== TOPS =============================== */

    {
      key: "mens_junior_top",
      label: "Men's / Junior Top",
      tags: ["Mens Tops", "Junior Tops"],
      primary: "chest",
      profile: "mens",
      measures: [
        { key: "chest", name: "Chest" },
        { key: "waist", name: "Waist" }
      ],
      sizes: [
        { size: "JS",  chest: [64, 68],   waist: [56, 60] },
        { size: "JM",  chest: [68, 72],   waist: [60, 64] },
        { size: "JL",  chest: [72, 76],   waist: [64, 68] },
        { size: "JXL", chest: [76, 84],   waist: [68, 72] },
        { size: "XS",  chest: [84, 89],   waist: [70, 75] },
        { size: "S",   chest: [89, 94],   waist: [75, 80] },
        { size: "M",   chest: [94, 99],   waist: [80, 85] },
        { size: "L",   chest: [99, 104],  waist: [85, 90] },
        { size: "XL",  chest: [104, 109], waist: [90, 95] },
        { size: "2XL", chest: [109, 114], waist: [95, 100] },
        { size: "3XL", chest: [114, 119], waist: [100, 105] },
        { size: "4XL", chest: [119, 124], waist: [105, 110] }
      ]
    },

    {
      key: "womens_top",
      label: "Women's Top",
      tags: ["Womens Tops"],
      primary: "chest",
      profile: "womens",
      measures: [
        { key: "chest", name: "Bust" },
        { key: "waist", name: "Waist" },
        { key: "hip",   name: "Hip" }
      ],
      sizes: [
        { size: "XS",  bracket: "UK 8",  chest: [79, 84],   waist: [62, 67],  hip: [87, 92] },
        { size: "S",   bracket: "UK 10", chest: [84, 89],   waist: [67, 72],  hip: [92, 97] },
        { size: "M",   bracket: "UK 12", chest: [89, 94],   waist: [72, 77],  hip: [97, 102] },
        { size: "L",   bracket: "UK 14", chest: [94, 99],   waist: [77, 82],  hip: [102, 107] },
        { size: "XL",  bracket: "UK 16", chest: [99, 104],  waist: [82, 87],  hip: [107, 112] },
        { size: "XXL", bracket: "UK 18", chest: [104, 109], waist: [87, 92],  hip: [112, 117] },
        { size: "3XL", bracket: "UK 20", chest: [109, 114], waist: [92, 97],  hip: [117, 122] },
        { size: "4XL", bracket: "UK 22", chest: [114, 119], waist: [97, 102], hip: [122, 127] }
      ]
    },

    {
      key: "unisex_top",
      label: "Unisex Top",
      tags: ["Unisex Tops"],
      primary: "chest",
      profile: "unisex",
      measures: [
        { key: "chest", name: "Chest" },
        { key: "waist", name: "Waist" }
      ],
      sizes: [
        { size: "JS",  chest: [60, 65],   waist: [56, 60] },
        { size: "JM",  chest: [65, 70],   waist: [60, 64] },
        { size: "JL",  chest: [70, 75],   waist: [64, 68] },
        { size: "JXL", chest: [75, 80],   waist: [68, 72] },
        { size: "XS",  chest: [86, 91],   waist: [72, 77] },
        { size: "S",   chest: [91, 96],   waist: [77, 82] },
        { size: "M",   chest: [96, 101],  waist: [82, 87] },
        { size: "L",   chest: [101, 106], waist: [87, 92] },
        { size: "XL",  chest: [106, 111], waist: [92, 97] },
        { size: "XXL", chest: [111, 116], waist: [97, 102] },
        { size: "3XL", chest: [116, 121], waist: [102, 107] },
        { size: "4XL", chest: [121, 126], waist: [107, 112] }
      ]
    },

    /* ============================= BOTTOMS ============================= */

    {
      key: "mens_junior_bottoms",
      label: "Men's / Junior Bottoms & Shorts",
      tags: ["Mens Bottoms", "Junior Bottoms", "Men's Shorts"],
      primary: "waist",
      profile: "mens",
      measures: [
        { key: "waist", name: "Waist" },
        { key: "hip",   name: "Hip" }
      ],
      sizes: [
        { size: "JS",  waist: [55, 58],   hip: [62, 66] },
        { size: "JM",  waist: [58, 62],   hip: [66, 70] },
        { size: "JL",  waist: [62, 66],   hip: [70, 74] },
        { size: "JXL", waist: [66, 71],   hip: [74, 78] },
        { size: "XS",  waist: [69, 74],   hip: [84, 89] },
        { size: "S",   waist: [74, 79],   hip: [89, 94] },
        { size: "M",   waist: [79, 84],   hip: [94, 99] },
        { size: "L",   waist: [84, 89],   hip: [99, 104] },
        { size: "XL",  waist: [89, 94],   hip: [104, 109] },
        { size: "2XL", waist: [94, 99],   hip: [109, 115] },
        { size: "3XL", waist: [99, 105],  hip: [115, 121] },
        { size: "4XL", waist: [105, 112], hip: [121, 127] }
      ]
    },

    {
      key: "womens_bottoms",
      label: "Women's Bottoms",
      /* NOTE: leggings are NOT this chart - they have their own sizing.
         See the "womens_leggings" placeholder at the bottom of this file. */
      tags: ["Womens Bottoms", "Women's Skorts/Shorts"],
      /* Women's skirts/trousers are designated by HIP under ISO 8559-2 -
         the opposite convention to menswear, where waist leads. */
      primary: "hip",
      profile: "womens",
      measures: [
        { key: "waist", name: "Waist" },
        { key: "hip",   name: "Hip" }
      ],
      sizes: [
        { size: "JS",  waist: [56, 60],   hip: [64, 69] },
        { size: "JM",  waist: [60, 64],   hip: [69, 74] },
        { size: "JL",  waist: [64, 68],   hip: [74, 79] },
        { size: "JXL", waist: [68, 72],   hip: [79, 84] },
        { size: "XS",  bracket: "UK 8",  waist: [62, 67],   hip: [87, 92] },
        { size: "S",   bracket: "UK 10", waist: [67, 72],   hip: [92, 97] },
        { size: "M",   bracket: "UK 12", waist: [72, 77],   hip: [97, 102] },
        { size: "L",   bracket: "UK 14", waist: [77, 82],   hip: [102, 107] },
        { size: "XL",  bracket: "UK 16", waist: [82, 87],   hip: [107, 112] },
        { size: "XXL", bracket: "UK 18", waist: [87, 92],   hip: [112, 117] },
        { size: "3XL", bracket: "UK 20", waist: [92, 97],   hip: [117, 122] },
        { size: "4XL", bracket: "UK 22", waist: [97, 102],  hip: [122, 127] }
      ]
    },

    {
      key: "womens_leggings",
      /* Leggings are NOT sized like the skorts/shorts above - they use the
         waist + hip figures from the WOMENS sheet on the
         "NEW - Playing Shirts Sizing (AKI)" tab (columns D and E).
         Same numbers as playing_shirt_womens_new's waist/hip, by design. */
      label: "Women's Leggings",
      tags: ["Womens Leggings"],
      primary: "hip",
      profile: "womens",
      measures: [
        { key: "waist", name: "Waist" },
        { key: "hip",   name: "Hip" }
      ],
      sizes: [
        { size: "XS",    bracket: "UK 8",  waist: [71, 73.5],   hip: [89, 94] },
        { size: "S",     bracket: "UK 10", waist: [73.5, 79],   hip: [94, 99] },
        { size: "M",     bracket: "UK 12", waist: [79, 81.5],   hip: [99, 101.5] },
        { size: "L",     bracket: "UK 14", waist: [81.5, 86.5], hip: [101.5, 106.5] },
        { size: "XL",    bracket: "UK 16", waist: [86.5, 89],   hip: [106.5, 109] },
        { size: "XXL",   bracket: "UK 18", waist: [89, 96.5],   hip: [109, 117] },
        { size: "XXXL",  bracket: "UK 20", waist: [96.5, 104],  hip: [117, 124.5] },
        { size: "XXXXL", bracket: "UK 22", waist: [104, 112],   hip: [124.5, 132] }
      ]
    },

    {
      key: "flow_bottoms",
      /* Waist + hip are the shared unisex-bottoms body measurements.
         Inside leg is Flow's own - Flow is tapered and shorter than Therma. */
      label: "Unisex Flow Tracksuit Bottoms",
      tags: ["Flow Bottoms"],
      primary: "waist",
      profile: "unisex",
      measures: [
        { key: "waist", name: "Waist" },
        { key: "hip",   name: "Hip" },
        { key: "leg",   name: "Inside leg", info: true }
      ],
      sizes: [
        { size: "JS",  waist: [52, 56],   hip: [62, 66],   leg: 63.5 },
        { size: "JM",  waist: [56, 60],   hip: [66, 70],   leg: 65 },
        { size: "JL",  waist: [60, 64],   hip: [70, 74],   leg: 66.5 },
        { size: "JXL", waist: [64, 68],   hip: [74, 78],   leg: 68 },
        { size: "XS",  waist: [66, 71],   hip: [84, 89],   leg: 71 },
        { size: "S",   waist: [71, 76],   hip: [89, 94],   leg: 72 },
        { size: "M",   waist: [76, 81],   hip: [94, 99],   leg: 73 },
        { size: "L",   waist: [81, 86],   hip: [99, 104],  leg: 74 },
        { size: "XL",  waist: [86, 91],   hip: [104, 109], leg: 75 },
        { size: "2XL", waist: [91, 97],   hip: [109, 115], leg: 76.5 },
        { size: "3XL", waist: [97, 103],  hip: [115, 121], leg: 78 },
        { size: "4XL", waist: [103, 109], hip: [121, 127], leg: 79.5 }
      ]
    },

    {
      key: "therma_bottoms",
      /* KEEP THIS LAST so every more specific bottoms chart gets first
         refusal. NOTE: BestFit also attaches the very broad "Bottoms" tag
         to this chart. If you want products tagged only "Bottoms" to match
         here, add "Bottoms" to the tags array below. */
      label: "Unisex Therma Tracksuit Bottoms",
      tags: ["Therma Bottoms"],
      primary: "waist",
      profile: "unisex",
      measures: [
        { key: "waist", name: "Waist" },
        { key: "hip",   name: "Hip" },
        { key: "leg",   name: "Inside leg", info: true }
      ],
      sizes: [
        { size: "JS",  waist: [52, 56],   hip: [62, 66],   leg: 69.5 },
        { size: "JM",  waist: [56, 60],   hip: [66, 70],   leg: 71 },
        { size: "JL",  waist: [60, 64],   hip: [70, 74],   leg: 72.5 },
        { size: "JXL", waist: [64, 68],   hip: [74, 78],   leg: 74 },
        { size: "XS",  waist: [66, 71],   hip: [84, 89],   leg: 76 },
        { size: "S",   waist: [71, 76],   hip: [89, 94],   leg: 77 },
        { size: "M",   waist: [76, 81],   hip: [94, 99],   leg: 78 },
        { size: "L",   waist: [81, 86],   hip: [99, 104],  leg: 79 },
        { size: "XL",  waist: [86, 91],   hip: [104, 109], leg: 80 },
        { size: "2XL", waist: [91, 97],   hip: [109, 115], leg: 81.5 },
        { size: "3XL", waist: [97, 103],  hip: [115, 121], leg: 83 },
        { size: "4XL", waist: [103, 109], hip: [121, 127], leg: 84.5 }
      ]
    },

    /* ==================== NOT BODY-MEASUREMENT SIZED ====================
       These exist so the finder knows to stay hidden on these products
       rather than guessing. They are sized by shoe size or by garment
       measurement, which the estimator cannot work from.
       ==================================================================== */

    {
      key: "socks",
      label: "Socks",
      tags: ["Socks"],
      recommend: false,
      reason: "Socks are sized by shoe size, not body measurements."
    },

    {
      key: "bespoke_skorts",
      /* No tag listed in BestFit - add one here if you want it to trigger. */
      label: "Bespoke Skorts",
      tags: [],
      recommend: false,
      reason: "Bespoke skorts are listed by garment measurement, not body measurement."
    }

  ]
};
